/**
 * app/(app)/help.tsx — Help Center (WO-H1).
 *
 * The reference library: searchable "How do I…" guides + screen explainers, rendered from the
 * SAME help store the contextual "?" uses (parity by construction). Reachable from the More hub
 * and from any "?" surface. Works offline (defaults are bundled).
 *
 * Registered on the (app) stack in app/(app)/_layout.tsx (see MERGE-GUIDE-H1.md).
 */
import React, { useMemo, useState } from 'react';
import { View, StyleSheet, TextInput } from 'react-native';
import { Screen, Text, Card } from '../../src/components/ui';
import { theme } from '../../src/theme';
import { useHelp } from '../../src/help';
import { HelpBody } from '../../src/components/help';

export default function HelpCenterScreen() {
  const help = useHelp();
  const [query, setQuery] = useState('');

  const guides = help.guides;
  const entries = useMemo(() => (query.trim() ? help.searchEntries(query) : []), [help, query]);

  return (
    <Screen scroll>
      <Text variant="title">Help Center</Text>
      <Text variant="body" color="secondary">
        Step-by-step guides for the things you do most. Search a task, or pick a guide. Tap the “?” on
        any screen for help with just that screen.
      </Text>

      <TextInput
        value={query}
        onChangeText={setQuery}
        placeholder="Search help…"
        placeholderTextColor={theme.color.text.secondary}
        style={styles.search}
        accessibilityLabel="Search help"
        returnKeyType="search"
        autoCorrect={false}
      />

      {query.trim().length > 0 && (
        <View style={styles.section}>
          <Text variant="label" color="secondary">
            {`RESULTS (${entries.length})`}
          </Text>
          {entries.length === 0 ? (
            <Text variant="body" color="secondary">
              Nothing matched “{query.trim()}”. Try a simpler word, or browse the guides below.
            </Text>
          ) : (
            entries.map((e) => (
              <Card key={e.id}>
                <Text variant="subhead">{e.title}</Text>
                <HelpBody body={e.body} />
              </Card>
            ))
          )}
        </View>
      )}

      <View style={styles.section}>
        <Text variant="label" color="secondary">
          GUIDES
        </Text>
        {guides.map((g) => (
          <Card key={g.id}>
            <Text variant="subhead">{g.title}</Text>
            {g.summary && (
              <Text variant="body" color="secondary">
                {g.summary}
              </Text>
            )}
            <View style={styles.steps}>
              {g.steps.map((s, i) => (
                <View key={i} style={styles.step}>
                  <Text variant="data" color="link" style={styles.stepNum}>
                    {String(i + 1)}
                  </Text>
                  <View style={styles.stepBody}>
                    <Text variant="bodyStrong">{s.title}</Text>
                    <Text variant="body" color="secondary">
                      {s.body}
                    </Text>
                  </View>
                </View>
              ))}
            </View>
          </Card>
        ))}
      </View>

      <View style={styles.section}>
        <Text
          variant="bodyStrong"
          color="link"
          accessibilityRole="button"
          accessibilityLabel="Replay the first-run walkthroughs"
          onPress={() => help.resetSeen()}
          style={styles.replay}
        >
          Replay the first-run walkthroughs
        </Text>
      </View>
    </Screen>
  );
}

const styles = StyleSheet.create({
  search: {
    minHeight: theme.layout.fieldHeight,
    borderWidth: theme.layout.hairline,
    borderColor: theme.color.border.strong,
    borderRadius: theme.radius.sm,
    paddingHorizontal: theme.spacing.md,
    backgroundColor: theme.color.bg.surface,
    color: theme.color.text.primary,
    ...theme.type.body,
  },
  section: { gap: theme.spacing.sm },
  steps: { gap: theme.spacing.md, marginTop: theme.spacing.sm },
  step: { flexDirection: 'row', gap: theme.spacing.md },
  stepNum: { width: theme.spacing.xl, textAlign: 'center' },
  stepBody: { flex: 1, gap: theme.spacing.xs },
  replay: { minHeight: theme.layout.minTapTarget, textAlignVertical: 'center' },
});
